/*
 * IcInventurZaehlbedarf.tsx
 *
 * Created by Dr. Maximillian Dornseif 2023-01-01 in huWaWi3 31.12.1
 * Copyright (c) 2023 HUDORA GmbH
 */

import { HdErrorBoundary3 } from '@hudora/hd-error-boundary'
import { LinkRender } from '../lib/hd-link'
import { LadeChecker } from '@hudora/hd-react-components'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { assert, assertIsString } from 'assertate-debug'
import { useTitle } from 'fluentui-hooks'
import gql from 'graphql-tag'
import React, { useState } from 'react'
import { useMutation, useQuery } from 'urql'

import { KindLightList } from '../eAP/KindLightList'
import { INVENTURBESTAETIGUNG_FRAGMENT } from '../fragments'
import { BqGrid } from '../lib/components/BqGrid'
import { INVENTUR_NAME } from './IcInventurApp'
import { EINEBESTAETIGUNG_QUERY } from './IcInventurQueries'

export const IcInventurZaehlbedarf = () => {
  useTitle(`Zählbedürftig`)
  const [saveData, setSaveData] = useState<Record<string, boolean | null | string | Number | Date>[]>([])
  const [result] = useQuery({
    query: BEBUCHT_QUERY,
  })
  const { data, fetching, error } = result
  console.log(data, fetching, error)

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4}>
        <div>
          <h2>1. Zählbedarf ermittelt</h2>
          <small>{data?.bqLagerBebucht?.rows?.length} Einträge</small>
          <HdErrorBoundary3>
            <table className="hd-table">
              <thead>
                <tr>
                  <th>Lager</th>
                  <th>SKU</th>
                </tr>
              </thead>
              <tbody>
                {data?.bqLagerBebucht?.rows?.map((row) => (
                  <tr key={`${row.lager}_${row.artnr}`}>
                    <td>{row.lager}</td>
                    <td>{row.artnr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </HdErrorBoundary3>

          <HdErrorBoundary3>
            <LadeChecker loading={fetching} error={error} label={`Lade Statistik`}>
              <BqGrid title="Zählbedürftig" result={data?.bqLagerBebucht} />
            </LadeChecker>
          </HdErrorBoundary3>
        </div>
        <div>
          <h2>2. Zählbedarf notieren</h2>
          <Button
            variant="contained"
            onClick={() => {
              setSaveData(data?.bqLagerBebucht?.rows)
            }}
          >
            Zählbedarf notieren
          </Button>
          {saveData.length > 0 ? (
            <div>
              <p>Zu speichernde Zählergebnisse:</p>
              <HdErrorBoundary3>
                <table className="hd-table">
                  <thead>
                    <tr>
                      <th>Lager</th>
                      <th>SKU</th>
                      <th>Status</th>
                      <th>ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {saveData.map((row) => (
                      <HdErrorBoundary3>
                        <SaveRow key={`${row.lager}_${row.artnr}`} row={row} />
                      </HdErrorBoundary3>
                    ))}
                  </tbody>
                </table>
              </HdErrorBoundary3>
            </div>
          ) : null}
        </div>
        <div>
          <h2>3. Zählbedarf notiert</h2>
          <HdErrorBoundary3>
            <IcInventurNotiert />
          </HdErrorBoundary3>
        </div>
      </Stack>
    </Stack>
  )
}

const blacklist = []
function SaveRow(props) {
  const [rowState, setRowState] = useState({ status: 'wait', ...props.row })
  const key = `${props.row.lager}_${props.row.artnr}`
  assertIsString(props.row.lager)
  assertIsString(props.row.artnr)
  const [result] = useQuery({
    query: EINEBESTAETIGUNG_QUERY,
    variables: {
      inventurName: INVENTUR_NAME,
      lager: props.row.lager,
      sku: `${props.row.artnr}`,
      cursor: null,
    },
    pause: rowState.status !== 'wait',
  })
  const { data } = result
  const eineBestaetigung = data?.nodes?.edges?.[0]?.node
  console.log('rowState', key, rowState)

  if (eineBestaetigung && !['done', 'saved'].includes(rowState.status)) {
    assert(
      data?.nodes?.edges?.length < 2,
      `zu viele Ergebnisse ${data?.nodes?.edges?.length} für ${props.row}`
    )
    assert(eineBestaetigung.artnr === `${props.row.artnr}`, `${eineBestaetigung.artnr} === ${rowState.artnr}`)
    assert(eineBestaetigung.lager === rowState.lager, `${eineBestaetigung.lager} === ${rowState.lager}`)
    assert(eineBestaetigung.inventur === INVENTUR_NAME, `${eineBestaetigung.inventur} === ${INVENTUR_NAME}`)
    if (rowState.status !== 'done') {
      setRowState({ ...rowState, status: 'done' })
    }
  } else {
    if (data?.nodes?.edges?.length < 1 && rowState.status !== 'save') {
      setRowState({ ...rowState, status: 'save' })
    }
  }
  const [mutateResult, mutateEntity] = useMutation(
    gql`
      mutation CreateInventur($artnr: String!, $lager: String!, $inventur: String!) {
        hwIcIntventurBestaetigungCreate(input: { lager: $lager, inventur: $inventur, artnr: $artnr }) {
          ...intventurBestaetigungFragment
        }
      }
      ${INVENTURBESTAETIGUNG_FRAGMENT}
    `
  )

  if (rowState.status === 'save') {
    const key = `${rowState.lager}_${rowState.artnr}`
    if (blacklist.includes(key)) {
      console.log('dupe', key)
    } else {
      console.log('saving', rowState.artnr)
      blacklist.push(key)
      setRowState({ ...rowState, status: 'saving' })
      mutateEntity({
        inventur: INVENTUR_NAME,
        lager: rowState.lager,
        artnr: rowState.artnr,
      })
    }
  }
  const { data: mutateData, fetching, error } = mutateResult
  if (error && rowState.status === 'saving') {
    console.error('error', error)
    setRowState({ ...rowState, status: 'error' })
  } else if (mutateData && !fetching) {
    setRowState({
      ...rowState,
      status: 'saved',
      designator: mutateData?.hwIcIntventurBestaetigungCreate?.designator,
    })
  }

  if (rowState.status === 'done') {
    return null
  }
  return (
    <tr key={key}>
      <td>{props.row.lager}</td>
      <td>{props.row.artnr}</td>
      <td>{rowState.status}</td>
      <td>
        <LinkRender value={eineBestaetigung?.designator} />
      </td>
    </tr>
  )
}

const IcInventurNotiert = () => {
  const [result] = useQuery({
    query: NOTIERT_QUERY,
    variables: { inventurName: INVENTUR_NAME },
  })
  const { data, fetching, error } = result
  let entities = []
  if (data?.nodes?.edges) {
    entities = data?.nodes?.edges.map((x) => x?.node)
  }
  return (
    <LadeChecker loading={fetching} data={data?.nodes} error={error}>
      <small>{entities.length} Einträge</small>
      <KindLightList
        entities={entities}
        schemaName={'HwIcIntventurBestaetigung'}
        hideExcept={['lager', 'zaehlbestand', 'artnr']}
      />
    </LadeChecker>
  )
}
const NOTIERT_QUERY = gql`
  query IntventurNotiert($cursor: String, $inventurName: String) {
    nodes: hwIcIntventurBestaetigungPages(
      after: $cursor
      filter: { inventur: { eq: $inventurName } }
      sort: { fields: [lager, artnr], order: ASC }
      limit: 10000
    ) {
      edges {
        cursor
        node {
          artnr
          lager
          zaehlbestand
          gezaehlt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`
const BEBUCHT_QUERY = gql`
  query BebuchtQuery {
    bqLagerBebucht {
      rows {
        artnr
        lager
        year
      }
      meta {
        configuration {
          jobType
          query {
            query
          }
        }
        statistics {
          creationTime
          endTime
          startTime
          totalBytesProcessed
        }
        jobReference {
          jobId
          location
          projectId
        }
      }
    }
  }
`
